import { useEffect } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import useStore from '../../store/useStore';
import apiClient from '../../services/api';
import { getProfiles } from '../../utils/connectorConfig';

function AppFooter() {
  return (
    <footer className="min-h-14 border-t border-gray-800 bg-gray-900 px-3 sm:px-6 py-2 sm:py-0 flex items-center flex-shrink-0">
      <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3">

        {/* Left – EU flag + funding text */}
        <div className="flex items-center gap-3 min-w-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 810 540" className="w-9 h-6 flex-shrink-0 rounded shadow-sm">
            <rect width="810" height="540" fill="#003399"/>
            {[0,1,2,3,4,5,6,7,8,9,10,11].map(i => {
              const angle = (i * 30 - 90) * Math.PI / 180;
              const cx = 405 + 162 * Math.cos(angle);
              const cy = 270 + 162 * Math.sin(angle);
              return (
                <polygon key={i}
                  points="0,-18 4.5,-6.3 17.1,-5.6 7.4,3.2 10.5,16 0,8.8 -10.5,16 -7.4,3.2 -17.1,-5.6 -4.5,-6.3"
                  fill="#FFCC00"
                  transform={`translate(${cx},${cy})`}
                />
              );
            })}
          </svg>
          <p className="text-xs text-gray-500 leading-relaxed text-center sm:text-left">
            Funded by the European Union · Grant Agreement No.&nbsp;101135423 ·{' '}
            <span className="hidden md:inline">Views and opinions expressed are those of the authors only.</span>
          </p>
        </div>

        {/* Right – copyright + social links */}
        <div className="flex items-center gap-4">
          <p className="hidden sm:block text-xs text-gray-600">© 2024 ENACT Consortium</p>
          <div className="flex items-center gap-3">
            {/* LinkedIn */}
            <a href="https://www.linkedin.com/in/enact-horizon-1798122b8/" target="_blank" rel="noopener noreferrer" title="LinkedIn"
              className="text-gray-500 hover:text-gray-300 transition-colors">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            {/* X */}
            <a href="https://x.com/ENACT_horizon" target="_blank" rel="noopener noreferrer" title="X (Twitter)"
              className="text-gray-500 hover:text-gray-300 transition-colors">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            {/* YouTube */}
            <a href="https://www.youtube.com/@ENACT-INNO" target="_blank" rel="noopener noreferrer" title="YouTube"
              className="text-gray-500 hover:text-gray-300 transition-colors">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const { sidebarOpen, setSidebarOpen, connectorKey } = useStore();

  useEffect(() => {
    if (sessionStorage.getItem('dataPoolBackfillDone')) return;
    sessionStorage.setItem('dataPoolBackfillDone', '1');
    getProfiles()
      .filter(p => p.providerEdcUrl || p.providerDspUrl)
      .forEach(p => {
        apiClient.post('/data-pool/register-mine', {
          name: p.name,
          providerEdcUrl: p.providerEdcUrl,
          providerDspUrl: p.providerDspUrl,
        }).catch(() => { /* best-effort */ });
      });
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 flex">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col min-w-0">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <main key={connectorKey} className="flex-1 p-3 sm:p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
        <AppFooter />
      </div>
    </div>
  );
}
